import { z } from 'zod';
import { MediaTypeEnum } from './media-unit-variants.js';
import { MediaUnitFiltersSchema } from './media-units.js';
import { endpoint } from '../api/endpoint.js';

export const BULK_EDIT_CAP = 500;

// ─── Selection union ──────────────────────────────────────────
// Exactly one of ids or filter must be present (discriminated union).

const IdSelectionSchema = z
  .object({
    ids: z.array(z.number().int().positive()).min(1),
  })
  .strict();

const FilterSelectionSchema = z
  .object({
    filter: MediaUnitFiltersSchema,
  })
  .strict();

export const BulkEditSelectionSchema = z.union([IdSelectionSchema, FilterSelectionSchema]);
export type BulkEditSelection = z.infer<typeof BulkEditSelectionSchema>;

// ─── Per-field discriminated request union ────────────────────

export const LifecycleStatusEnum = z.enum(['active', 'inactive', 'archived']);
export type LifecycleStatus = z.infer<typeof LifecycleStatusEnum>;

const MediaTypeFieldSchema = z.object({
  field: z.literal('mediaType'),
  value: MediaTypeEnum,
});

const CityFieldSchema = z.object({
  field: z.literal('city'),
  value: z.string().min(1),
});

const LifecycleStatusFieldSchema = z.object({
  field: z.literal('lifecycleStatus'),
  value: LifecycleStatusEnum,
});

const SupplierFieldSchema = z.object({
  field: z.literal('supplier'),
  value: z.number().int().positive(),
});

const BulkEditFieldValueSchema = z.discriminatedUnion('field', [
  MediaTypeFieldSchema,
  CityFieldSchema,
  LifecycleStatusFieldSchema,
  SupplierFieldSchema,
]);

// ─── Full request schema ──────────────────────────────────────

export const bulkEditRequestSchema = BulkEditFieldValueSchema.and(
  z.object({ selection: BulkEditSelectionSchema }),
);

export type BulkEditRequest = z.infer<typeof bulkEditRequestSchema>;

// ─── Response schema ──────────────────────────────────────────

export const bulkEditResponseSchema = z.object({
  updated: z.number().int().min(0),
  failed: z.number().int().min(0),
  failedIds: z.array(z.number().int().positive()).optional(),
});

export type BulkEditResponse = z.infer<typeof bulkEditResponseSchema>;

// ─── Endpoint definition ──────────────────────────────────────

export const bulkEditEndpoint = endpoint(
  'POST',
  '/media-units/bulk-edit',
  bulkEditRequestSchema,
  bulkEditResponseSchema,
);
